import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Grid,
  Paper, 
  Button, 
  Box,
  Card,
  CardContent,
  CardActions,
  Divider,
  CircularProgress
} from '@mui/material';

import { 
  Inventory as InventoryIcon,
  LocalOffer as PriceIcon,
  Label as LabelIcon,
  ShoppingCart as CartIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

const StoreDashboard: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalItems: 0,
    lowStockItems: 0,
    labelsToPrint: 0,
    lastInventoryUpdate: null as Date | null
  });

  useEffect(() => {
    // In a real implementation, you would fetch data from your database here
    // For now we'll just initialize with empty state
    setLoading(false);
  }, []);

  const quickActions = [
    { 
      title: 'Update Inventory', 
      icon: <InventoryIcon fontSize="large" />, 
      description: 'Count and update stock levels',
      path: '/staff/store/inventory/update' 
    },
    { 
      title: 'Print Labels', 
      icon: <LabelIcon fontSize="large" />, 
      description: 'Generate price tags and labels',
      path: '/staff/store/labels' 
    },
    { 
      title: 'Check Pricing', 
      icon: <PriceIcon fontSize="large" />, 
      description: 'Look up or update item prices',
      path: '/staff/store/pricing' 
    },
    { 
      title: 'Process Sale', 
      icon: <CartIcon fontSize="large" />, 
      description: 'Record a transaction',
      path: '/staff/store/transaction' 
    }
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Store Dashboard
      </Typography>
      <Typography variant="h6" gutterBottom>
        Welcome, {userProfile?.firstName || 'Staff Member'}
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                <Typography variant="h5" color="primary">{stats.totalItems}</Typography>
                <Typography variant="body2">Total Items</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 2, 
                  textAlign: 'center', 
                  height: '100%',
                  backgroundColor: stats.lowStockItems > 0 ? 'rgba(255, 152, 0, 0.1)' : 'inherit'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="h5" color={stats.lowStockItems > 0 ? 'warning.main' : 'primary'}>
                    {stats.lowStockItems}
                  </Typography>
                  {stats.lowStockItems > 0 && <WarningIcon color="warning" />}
                </Box>
                <Typography variant="body2">Low Stock Items</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                <Typography variant="h5" color="primary">{stats.labelsToPrint}</Typography>
                <Typography variant="body2">Labels to Print</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper elevation={3} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
                <Typography variant="h5" color="primary">
                  {stats.lastInventoryUpdate ? 
                    stats.lastInventoryUpdate.toLocaleDateString() : 
                    'Never'}
                </Typography>
                <Typography variant="body2">Last Inventory Update</Typography>
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
            Quick Actions
          </Typography>
          
          <Grid container spacing={3}>
            {quickActions.map((action) => (
              <Grid item key={action.title} xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ textAlign: 'center', flexGrow: 1 }}>
                    <Box sx={{ mb: 2, color: 'primary.main' }}>
                      {action.icon}
                    </Box>
                    <Typography variant="h6" component="div">
                      {action.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {action.description}
                    </Typography>
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Button 
                      size="small" 
                      fullWidth
                      onClick={() => navigate(action.path)}
                    >
                      Go
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default StoreDashboard; 